import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../helpers/api';
import { ENDPOINTS } from '../helpers/constants';
import { copyToClipboard, getBusinessDaysDiff, getUserFromLocalstrorage, isClient } from '../helpers/global';
import { toPlainTextFromRichText } from '../helpers/safeHtml';
import { getBizDaysLabel } from '../components/tasks/taskCardUtils';

const useTaskCard = ({ task, onStatusUpdate, fromDetails = false }) => {
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showQualiaConfirm, setShowQualiaConfirm] = useState(false);
  const role = useMemo(() => localStorage.getItem('user-role'), []);
  const isClientUser = useMemo(() => isClient(role), [role]);
  const currentUserId = useMemo(() => getUserFromLocalstrorage()?._id, []);
  const canDeleteTask = useMemo(() => {
    const creatorId = task?.createdBy?._id || task?.createdBy;
    return Boolean(creatorId && currentUserId && String(creatorId) === String(currentUserId));
  }, [task?.createdBy, currentUserId]);

  const excludeFridays = Boolean(task?.excludeFridays);
  const businessDays = useMemo(
    () => getBusinessDaysDiff(moment(task?.createdAt), moment(task?.dueDate), excludeFridays),
    [task?.createdAt, task?.dueDate, excludeFridays]
  );
  const bizDaysLabel = getBizDaysLabel(businessDays);
  const assignedProcessors = task?.assignedProcessors || [];
  const orderType = task?.order?.orderType || 'Other';
  const serviceId = task?.order?.service?._id;
  const isComplete = task?.status === 'Complete';
  const isQualiaTask = Boolean(task?.qualiaType);

  const onHandleTaskStatus = useCallback(
    async (status, { qualiaSentConfirmed = false } = {}) => {
      setUpdatingStatus(true);
      try {
        const requestBody = { order: task.order?._id, status };
        if (status === 'Complete' && qualiaSentConfirmed) {
          requestBody.qualiaSentConfirmed = true;
        }
        const { response, error } = await makeRESTApiRequest({
          endpoint: ENDPOINTS.TASKS_WITH_ID(task._id),
          method: 'PUT',
          requestBody
        });
        if (error) {
          toast.error(error);
          return;
        }
        onStatusUpdate(response);
        toast.success('Status updated successfully');
        setShowQualiaConfirm(false);
      } catch (e) {
        toast.error(e);
      } finally {
        setUpdatingStatus(false);
      }
    },
    [task._id, task.order?._id, onStatusUpdate]
  );

  const toggleTaskStatus = useCallback(() => {
    if (isComplete) {
      onHandleTaskStatus('Incomplete');
      return;
    }
    if (isQualiaTask) {
      setShowQualiaConfirm(true);
      return;
    }
    onHandleTaskStatus('Complete');
  }, [isComplete, isQualiaTask, onHandleTaskStatus]);

  const confirmQualiaSent = useCallback(() => {
    onHandleTaskStatus('Complete', { qualiaSentConfirmed: true });
  }, [onHandleTaskStatus]);

  const cancelQualiaConfirm = useCallback(() => setShowQualiaConfirm(false), []);

  const copyQualiaMessage = useCallback(
    async event => {
      event?.stopPropagation?.();
      const ok = await copyToClipboard(toPlainTextFromRichText(task?.description || ''));
      toast[ok ? 'success' : 'error'](ok ? 'Message copied' : 'Could not copy message');
    },
    [task?.description]
  );

  const openServiceInNewTab = useCallback(() => {
    if (fromDetails || !serviceId) return;
    const params = new URLSearchParams();
    if (orderType) params.set('OrderType', orderType);
    const query = params.toString();
    window.open(`/services/${serviceId}${query ? `?${query}` : ''}`, '_blank');
  }, [fromDetails, serviceId, orderType]);

  return {
    isClientUser,
    canDeleteTask,
    updatingStatus,
    toggleTaskStatus,
    bizDaysLabel,
    assignedProcessors,
    orderType,
    serviceId,
    isComplete,
    openServiceInNewTab,
    isQualiaTask,
    showQualiaConfirm,
    confirmQualiaSent,
    cancelQualiaConfirm,
    copyQualiaMessage
  };
};

export default useTaskCard;
